import React, { useState } from 'react';
import { withRouter } from 'react-router';
import merge from 'lodash/merge';
import DiscoveryContext from './contextV2';
import { useLocale } from '../../hooks/useLocale';
import { stringifyQS } from '../../utils';
import history from '@/routes/routes/helpers/history';
import { Filters } from './utils/filtersEnum';
import LocationFilterProvider from './providers/locationProvider';
import DistancesFilterProvider from './providers/distanceProvider';
import DateFilterProvider from './providers/dateProvider';
import SortProvider from './providers/sortProvider';
import SportProvider from './providers/sportProvider';
import TagFilterProvider from './providers/tagProvider';
import NameProvider from './providers/nameProvider';
import CovidProvider from './providers/covidProvider';

const availableFilterProviders = [
  {
    type: Filters.Location,
    provider: LocationFilterProvider,
  },
  {
    type: Filters.Distance,
    provider: DistancesFilterProvider,
  },
  {
    type: Filters.Date,
    provider: DateFilterProvider,
  },
  {
    type: Filters.Sort,
    provider: SortProvider,
  },
  {
    type: Filters.Sport,
    provider: SportProvider,
  },
  {
    type: Filters.Tag,
    provider: TagFilterProvider,
  },
  {
    type: Filters.Name,
    provider: NameProvider,
  },
  {
    type: Filters.Covid,
    provider: CovidProvider,
  },
];

const ProviderV2 = ({ router, children }) => {
  const locale = useLocale();

  const [filters, setFilters] = useState(
    merge({}, ...Object.values(Filters).map((value) => ({ [value]: {} }))),
  );

  const updateFilter = ({ key, value }) => {
    setFilters((prevState) => ({ ...prevState, [key]: value }));
  };

  // eslint-disable-next-line no-unused-vars
  const [providers, setProviders] = useState(
    availableFilterProviders.map((n) => ({
      type: n.type,
      ops: n.provider({
        updateFilter,
      }),
    })),
  );

  const updateCategoryData = ({ categoryData }) => {
    const categoryDataWithoutTypename = categoryData
      ? JSON.parse(JSON.stringify(categoryData), (key, value) =>
          key === '__typename' ? undefined : value,
        )
      : categoryData;

    providers.forEach(({ ops }) => {
      ops.update({
        categoryData: categoryDataWithoutTypename || {},
        queryParams: router?.location?.query,
        locale,
      });
    });
  };

  const queryParams = () => {
    const providerQueryParams = providers.map(({ ops }) =>
      ops.toQueryParams?.(),
    );
    return merge({}, ...providerQueryParams);
  };

  const getLinkWithFilters = (obj) => {
    let path = router.location.pathname;

    const providerPath = providers
      .map(({ ops }) => ops.pushHistory?.(obj))
      .filter(Boolean)[0];

    if (providerPath) {
      path = providerPath;
    }

    const search = `?${stringifyQS({ ...queryParams(), ...obj })}`;

    return {
      path,
      search,
      url: `${path}${search}`,
    };
  };

  const pushHistory = (obj) => {
    const { path, search } = getLinkWithFilters(obj);

    history.push({
      pathname: path,
      search,
    });
  };

  const exposedProviderValues = merge(
    {},
    ...providers.map(({ ops }) => ops.exposed),
  );

  const activeFilters = providers.reduce((acc, { type, ops }) => {
    const active = ops?.hasActiveFilters?.();
    if (active !== undefined) {
      acc[type] = active;
    }

    return acc;
  }, {});

  const defaultFilters = providers.reduce((acc, { type, ops }) => {
    const defaultFilter = ops?.defaultFilters;
    if (defaultFilter !== undefined) {
      acc[type] = defaultFilter;
    }

    return acc;
  }, {});

  const providerValue = {
    pushHistory,
    queryParams,
    getLinkWithFilters,
    updateCategoryData,
    filters,
    activeFilters,
    defaultFilters,
    operations: exposedProviderValues,
  };

  return (
    <DiscoveryContext.Provider value={providerValue}>
      {children}
    </DiscoveryContext.Provider>
  );
};

export default withRouter(ProviderV2);
